import type { DocumentLink } from "@/models/document-link/DocumentLink";
import {
  getDocumentTypeDisplayName,
  getDocumentTypeInfo,
  getDocumentTypeName,
  getDocumentTypePopularName,
} from "@/utils/documentTypeMapping";
import { getOtherSideLegislationTypeId } from "@/utils/documentLinkOtherSide";
import { parseReferencesSidebarExtraMetadata } from "@/utils/documentLinkReferencesSidebarVisibility";

export type ReferenceLegislationGroup = {
  groupKey: string;
  label: string;
  links: DocumentLink[];
};

const UNKNOWN_LEGISLATION_TYPE_KEY = "__unknown__";

/** Retsinformation reference group title when present on the link. */
export function legislationLinkHeader(link: DocumentLink): string | undefined {
  const header = parseReferencesSidebarExtraMetadata(link.extraMetadata).header?.trim();
  return header || undefined;
}

export function partitionLegislationLinksByHeader(links: DocumentLink[]): {
  withHeader: DocumentLink[];
  withoutHeader: DocumentLink[];
} {
  const withHeader: DocumentLink[] = [];
  const withoutHeader: DocumentLink[] = [];
  for (const link of links) {
    if (legislationLinkHeader(link)) {
      withHeader.push(link);
    } else {
      withoutHeader.push(link);
    }
  }
  return { withHeader, withoutHeader };
}

function headerGroupKey(header: string): string {
  return `header-${header.replace(/\s+/g, "-").toLowerCase()}`;
}

function otherSideLegislationTypeId(
  link: DocumentLink,
  isOutgoing: boolean
): string {
  return (
    getOtherSideLegislationTypeId(link, isOutgoing) ??
    UNKNOWN_LEGISLATION_TYPE_KEY
  );
}

function legislationTypeSubgroupLabel(typeId: string): string {
  if (typeId === UNKNOWN_LEGISLATION_TYPE_KEY) {
    return "Ukendt lovtype";
  }
  return (
    getDocumentTypePopularName(typeId) ??
    getDocumentTypeName(typeId) ??
    getDocumentTypeDisplayName(typeId)
  );
}

function legislationTypeSubgroupSortOrder(typeId: string): number {
  if (typeId === UNKNOWN_LEGISLATION_TYPE_KEY) return 1_000_000;
  return getDocumentTypeInfo(typeId)?.SorteringsId ?? 999_999;
}

export function buildLegislationHeaderGroups(
  links: DocumentLink[],
  documentId: string,
  sortLinksForDisplay: (links: DocumentLink[], documentId: string) => DocumentLink[]
): ReferenceLegislationGroup[] {
  const byHeader = new Map<string, DocumentLink[]>();
  for (const link of links) {
    const header = legislationLinkHeader(link);
    if (!header) continue;
    const bucket = byHeader.get(header) ?? [];
    bucket.push(link);
    byHeader.set(header, bucket);
  }

  const groups: ReferenceLegislationGroup[] = [...byHeader.entries()].map(
    ([header, groupLinks]) => ({
      groupKey: headerGroupKey(header),
      label: header,
      links: sortLinksForDisplay(groupLinks, documentId),
    })
  );

  groups.sort((a, b) =>
    a.label.localeCompare(b.label, "da", { sensitivity: "base" })
  );
  return groups;
}

export function buildLegislationTypeGroups(
  links: DocumentLink[],
  documentId: string,
  isOutgoing: boolean,
  sortLinksForDisplay: (links: DocumentLink[], documentId: string) => DocumentLink[]
): ReferenceLegislationGroup[] {
  const byType = new Map<string, DocumentLink[]>();
  for (const link of links) {
    const typeId = otherSideLegislationTypeId(link, isOutgoing);
    const bucket = byType.get(typeId) ?? [];
    bucket.push(link);
    byType.set(typeId, bucket);
  }

  const groups: ReferenceLegislationGroup[] = [...byType.entries()].map(
    ([typeId, groupLinks]) => ({
      groupKey: `type-${typeId}`,
      label: legislationTypeSubgroupLabel(typeId),
      links: sortLinksForDisplay(groupLinks, documentId),
    })
  );

  groups.sort((a, b) => {
    const typeIdA = a.groupKey.replace(/^type-/, "");
    const typeIdB = b.groupKey.replace(/^type-/, "");
    const oa = legislationTypeSubgroupSortOrder(typeIdA);
    const ob = legislationTypeSubgroupSortOrder(typeIdB);
    if (oa !== ob) return oa - ob;
    return a.label.localeCompare(b.label, "da", { sensitivity: "base" });
  });
  return groups;
}

export type LegislationReferenceGroups = {
  headerGroups: ReferenceLegislationGroup[];
  typeGroups: ReferenceLegislationGroup[];
};

export function buildLegislationReferenceGroups(
  links: DocumentLink[],
  documentId: string,
  isOutgoing: boolean,
  sortLinksForDisplay: (links: DocumentLink[], documentId: string) => DocumentLink[]
): LegislationReferenceGroups {
  const { withHeader, withoutHeader } = partitionLegislationLinksByHeader(links);
  return {
    headerGroups: buildLegislationHeaderGroups(
      withHeader,
      documentId,
      sortLinksForDisplay
    ),
    typeGroups: buildLegislationTypeGroups(
      withoutHeader,
      documentId,
      isOutgoing,
      sortLinksForDisplay
    ),
  };
}

export function countLegislationReferenceLinks(
  groups: LegislationReferenceGroups
): number {
  return (
    groups.headerGroups.reduce((s, g) => s + g.links.length, 0) +
    groups.typeGroups.reduce((s, g) => s + g.links.length, 0)
  );
}
